/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React, { memo } from 'react'
import clsx from 'clsx'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { Notification } from 'types/entities/Notification'
import i18n from 'i18next'
import { BellIcon } from '@heroicons/react/24/outline'
import { getReminderModelConfig } from 'utils/reminders/reminderUtils'
import { getNotificationLinkRef } from 'utils/notifications/notificationEntity'

dayjs.extend(relativeTime)

interface NotificationItemProps {
    notification: Notification
    onClick: (notification: Notification) => void
    t: (key: string) => string
}

const UiNotificationsDropdownItem: React.FC<NotificationItemProps> = memo(
    ({ notification, onClick, t }) => {
        const isUnread = !notification.read_at
        const linkRef = getNotificationLinkRef(notification)
        const modelName = linkRef.entity?.modelName
        const modelId = linkRef.entity?.modelId
        const hasEntityBadge = Boolean(
            (modelName && modelId) || linkRef.reminderId
        )
        const modelConfig = modelName ? getReminderModelConfig(modelName) : null
        const badgeLabel = modelName && modelId
            ? `${t(modelConfig?.label ?? modelName)} #${modelId}`
            : linkRef.reminderId
              ? `${t('Reminder')} #${linkRef.reminderId}`
              : null

        return (
            <div
                onClick={() => onClick(notification)}
                className={clsx(
                    'UiNotificationsDropdownItem flex gap-3 px-4 py-3 text-sm transition-colors cursor-pointer border-b border-[var(--color-border)] last:border-0',
                    'hover:bg-[var(--color-hover)]',
                    isUnread
                        ? 'bg-blue-50/50 dark:bg-blue-900/10 font-semibold'
                        : 'bg-[var(--color-bg)]'
                )}
            >
                <div
                    className={clsx(
                        'mt-1 h-2 w-2 min-w-[0.5rem] rounded-full shrink-0',
                        isUnread
                            ? 'bg-[var(--color-alert-info)]'
                            : 'bg-transparent'
                    )}
                />
                <div className={clsx('flex flex-col flex-1 min-w-0')}>
                    <span className={clsx('text-sm break-words')}>
                        {notification.data?.message || t('No message')}
                    </span>
                    {notification.data?.sender && (
                        <span
                            className={clsx(
                                'text-xs text-gray-500 dark:text-gray-400 mt-1 truncate'
                            )}
                        >
                            {notification.data.sender}
                        </span>
                    )}
                    {hasEntityBadge && badgeLabel && (
                        <span
                            className={clsx(
                                'mt-1.5 self-start flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full',
                                modelConfig
                                    ? [modelConfig.colorClass, modelConfig.textColorClass]
                                    : 'bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300'
                            )}
                        >
                            <BellIcon className={clsx('size-3')} />
                            {badgeLabel}
                        </span>
                    )}
                    <span
                        className={clsx(
                            'mt-1 text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500'
                        )}
                    >
                        {dayjs(notification.created_at)
                            .locale(i18n.language)
                            .fromNow()}
                    </span>
                </div>
            </div>
        )
    }
)

export default UiNotificationsDropdownItem
